// Angular
import { Injectable } from '@angular/core';

// Third party
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Discriminated union returned by every read method below. A successful
 * read returns coords; a failure returns a status object the UI can
 * render directly. Identical shape to the Capacitor-based
 * `GeoLocationService` so the two can be swapped without churning
 * call-sites.
 */
export type GeoLocationWebResult =
  | { latitude: number; longitude: number }
  | { status: boolean; msg: string; showDeviceSetting: boolean };

@Injectable({
  providedIn: 'root'
})
export class GeoLocationWebService {

  private watchId: number | null = null;

  // observable for location (kept for backward compat with existing
  // consumers such as `bet.service.ts` which read latest coords via
  // `firstValueFrom(geo$)`).
  private geoSubject = new BehaviorSubject<{ latitude: number; longitude: number } | null>(null);
  geo$: Observable<{ latitude: number; longitude: number } | null> = this.geoSubject.asObservable();

  /**
   * Returns true if the runtime exposes the browser Geolocation API.
   * @returns {boolean}
   */
  isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'geolocation' in navigator &&
      navigator.geolocation != null
    );
  }

  /**
   * Query the Permissions API for the current geolocation permission
   * state without prompting the user. Falls back to `'prompt'` on
   * browsers that do not expose `navigator.permissions` (older Safari).
   * @returns {Promise<'granted' | 'denied' | 'prompt'>}
   */
  async checkPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    if (!this.isSupported()) return 'denied';

    if (
      typeof navigator === 'undefined' ||
      !navigator.permissions ||
      typeof navigator.permissions.query !== 'function'
    ) {
      return 'prompt';
    }

    try {
      const status = await navigator.permissions.query({
        name: 'geolocation' as PermissionName
      });
      return status.state as 'granted' | 'denied' | 'prompt';
    } catch {
      return 'prompt';
    }
  }

  /**
   * One-shot geolocation read. Resolves with coords on success or a
   * status union on failure (never rejects). Maps each
   * `GeolocationPositionError` code to a user-friendly message.
   * @returns {Promise<GeoLocationWebResult>}
   */
  async getLocation(): Promise<GeoLocationWebResult> {
    if (!this.isSupported()) {
      return {
        msg: 'Geolocation is not supported by this browser.',
        status: false,
        showDeviceSetting: false
      };
    }

    // Old: single high-accuracy read with no fallback. Failed on
    // desktop/macOS with POSITION_UNAVAILABLE even when permission granted.
    // return new Promise<GeoLocationWebResult>((resolve) => {
    //   navigator.geolocation.getCurrentPosition(
    //     (geo) => {
    //       const coords = {
    //         latitude: geo.coords.latitude,
    //         longitude: geo.coords.longitude
    //       };
    //       this.geoSubject.next(coords);
    //       this.startWatching();
    //       resolve(coords);
    //     },
    //     (err: GeolocationPositionError) => {
    //       resolve(this.mapPositionError(err));
    //     },
    //     {
    //       enableHighAccuracy: true,
    //       timeout: 15000,
    //       maximumAge: 0
    //     }
    //   );
    // });

    try {
      // High-accuracy first for the best precision.
      const geo = await this.readPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      });
      return this.onPositionSuccess(geo);
    } catch (err) {
      const posErr = err as GeolocationPositionError;
      // Desktop/OS providers often fail high-accuracy reads with
      // POSITION_UNAVAILABLE/TIMEOUT even when permission is granted.
      // Retry once with low accuracy and a cached position allowed.
      if (
        posErr &&
        (posErr.code === posErr.POSITION_UNAVAILABLE ||
          posErr.code === posErr.TIMEOUT)
      ) {
        try {
          const geo = await this.readPosition({
            enableHighAccuracy: false,
            timeout: 20000,
            maximumAge: 60000
          });
          return this.onPositionSuccess(geo);
        } catch (fallbackErr) {
          return this.mapPositionError(fallbackErr as GeolocationPositionError);
        }
      }
      return this.mapPositionError(posErr);
    }
  }

  /**
   * Promise wrapper around `getCurrentPosition` for a single options set.
   * @param {PositionOptions} options - Geolocation options.
   * @returns {Promise<GeolocationPosition>}
   */
  private readPosition(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  /**
   * Cache coords, start the background watch and return the success union.
   * @param {GeolocationPosition} geo - Successful position read.
   * @returns {GeoLocationWebResult}
   */
  private onPositionSuccess(geo: GeolocationPosition): GeoLocationWebResult {
    const coords = {
      latitude: geo.coords.latitude,
      longitude: geo.coords.longitude
    };
    this.geoSubject.next(coords);
    // Keep cached coords fresh without callers opting in explicitly.
    this.startWatching();
    return coords;
  }

  /**
   * Ask for geolocation permission. Backward-compatible: callers that
   * ignore the return value (e.g. `bet.service.ts`) and read `geo$`
   * instead keep working. New callers can use the rich return value
   * (same shape as the Capacitor `GeoLocationService`).
   * @param {boolean} skipPermissionRequest - When true, never trigger
   *  the browser's native permission dialog. Used by interval /
   *  cold-start / resume paths that must stay silent.
   * @returns {Promise<GeoLocationWebResult>}
   */
  async askPermissionGeoLocation(
    skipPermissionRequest: boolean = false
  ): Promise<GeoLocationWebResult> {
    if (!this.isSupported()) {
      return {
        msg: 'Geolocation is not supported by this browser.',
        status: false,
        showDeviceSetting: false
      };
    }

    const permState = await this.checkPermission();

    if (permState === 'granted') {
      return this.getLocation();
    }

    if (permState === 'denied') {
      if (skipPermissionRequest) {
        return { msg: '', status: false, showDeviceSetting: false };
      }
      return {
        msg:
          'Location access is blocked. Please allow location for this ' +
          'site in your browser settings, then try again.',
        status: false,
        showDeviceSetting: true
      };
    }

    // permState === 'prompt'
    if (skipPermissionRequest) {
      return { msg: '', status: false, showDeviceSetting: false };
    }

    // Triggers the browser's native permission dialog.
    return this.getLocation();
  }

  /**
   * Stop watching for geolocation updates.
   * @returns {void}
   */
  stopWatching(): void {
    if (this.watchId !== null && this.isSupported()) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /**
   * Synchronous snapshot of the latest known coords; `null` until the
   * first successful read.
   * @returns {{ latitude: number; longitude: number } | null}
   */
  getCachedCoords(): { latitude: number; longitude: number } | null {
    return this.geoSubject.value;
  }

  /**
   * Begin `watchPosition` only if not already watching, so subsequent
   * `getLocation` calls don't pile up watchers.
   * @returns {void}
   */
  private startWatching(): void {
    if (this.watchId !== null || !this.isSupported()) return;

    this.watchId = navigator.geolocation.watchPosition(
      (geo) => {
        this.geoSubject.next({
          latitude: geo.coords.latitude,
          longitude: geo.coords.longitude
        });
      },
      () => {
        // Background watch errors are non-fatal — the next foreground
        // read will surface the failure to the user.
      },
      // Old: forced fresh high-accuracy fixes, which spammed macOS/iOS
      // CoreLocation with kCLErrorLocationUnknown. Now allow cached,
      // low-accuracy fixes so the watch stops re-triggering that error.
      // {
      //   enableHighAccuracy: true,
      //   maximumAge: 5000
      // }
      {
        enableHighAccuracy: false,
        maximumAge: 120000
      }
    );
  }

  /**
   * Map a browser `GeolocationPositionError` code to a user-friendly
   * failure object.
   * @param {GeolocationPositionError} err - Browser geolocation error.
   * @returns {GeoLocationWebResult}
   */
  private mapPositionError(err: GeolocationPositionError): GeoLocationWebResult {
    if (err && err.code === err.PERMISSION_DENIED) {
      return {
        msg:
          'Location access is blocked. Please allow location for this ' +
          'site in your browser settings, then try again.',
        status: false,
        showDeviceSetting: true
      };
    }
    if (err && err.code === err.POSITION_UNAVAILABLE) {
      return {
        msg:
          'We could not determine your location. Please check that ' +
          'location services are enabled on your device and try again.',
        status: false,
        showDeviceSetting: false
      };
    }
    if (err && err.code === err.TIMEOUT) {
      return {
        msg: 'Locating you is taking too long. Please try again.',
        status: false,
        showDeviceSetting: false
      };
    }
    return {
      msg: 'We could not access your location. Please try again.',
      status: false,
      showDeviceSetting: false
    };
  }
}
